/* eslint-disable @typescript-eslint/no-explicit-any */

import { GridColDef } from '@mui/x-data-grid-premium';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ErrorType } from '../../api/errorHandling';

export type PageNameType =
 | 'results'
 | 'customers'
 | 'orders'
 | 'vehicles'
 | 'drivers'
 | 'products'
 | 'products/groups'
 | 'contracts'
 | 'contracts/details'
 | 'spy'
 | 'weightings'
 | 'destPoint'
 | 'destPoint/details'
 | 'cards'
 | 'cards/cardtimes'
 | 'settings'
 | 'identity'
 | 'licenses'
 | 'users'
 | 'accessGroups'
 | 'audit'
 | 'warehouse'
 | 'inventorization'
 | 'IoT'
 | 'dynamicTables'
 // runtime-created tables, e.g. 'dynamicTables/Inspections'
 | `dynamicTables/${string}`;

export type ClientConfigurationItem = {
 Id: string;
 Name: string;
 Parameter: string;
 Type: string;
 Value: string;
};

// COLUMNS, FILTERS

export type FilterFieldsType = {
 fields: {
  [key: string]:
   | undefined
   | null
   | string
   | number
   | { [key: string]: string | number }
   | Array<{ [key: string]: string | number }>;
 };
 operators: { [key: string]: string | null };
};

export type ColPresetType = {
 name: string;
 columns: GridColDef[];
 filters?: FilterFieldsType;
 isPro?: boolean;
 limit?: {
  [key: string]: Array<{
   limit: string;
   value: string;
   color: string;
  }>;
 };
};

// export type ColPresetsType = {
//  name: string;
//  data: ColPresetType[];
// };

// FIELDS

export type FieldPresetType = {
 [key: string]: {
  sidebar: string[];
  edit: string[];
 };
};

type ClientConfigurationState = {
 clientConfiguration: ClientConfigurationItem | null;
 clientConfigurations: ClientConfigurationItem[] | null;
 createdConfiguration: ClientConfigurationItem | null;
 updatedConfiguration: ClientConfigurationItem | null;
 deletedConfiguration: ClientConfigurationItem | null;
 loading: { [key: string]: boolean | null };
 success: { [key: string]: boolean | null };
 rowCount: string;
 presetsColumns: { [key: string]: ColPresetType[] } | null;
 currentColumnsPreset: { [key: string]: ColPresetType | null } | null;
 tempColumnsPreset: { [key: string]: ColPresetType | null } | null;
 fieldPresets: FieldPresetType | null;
};

// enum record

const initialState: ClientConfigurationState = {
 clientConfiguration: null,
 clientConfigurations: null,
 createdConfiguration: null,
 updatedConfiguration: null,
 deletedConfiguration: null,
 rowCount: '0',
 loading: {},
 success: {},
 presetsColumns: null,
 currentColumnsPreset: null,
 tempColumnsPreset: null,
 fieldPresets: null,
};

const hasValidObjectPayload = (value: unknown): value is Record<string, unknown> =>
 !!value && typeof value === 'object' && !Array.isArray(value);

const clientConfigurationSlice = createSlice({
 name: 'clientConfiguration',
 initialState,
 reducers: {
  // state

  changeCurrentPreset(
   state,
   action: PayloadAction<{
    page: PageNameType;
    type: 'columns' | 'fields';
    preset: ColPresetType;
    temp: boolean;
   }>,
  ) {
   const { page, type, preset, temp } = action.payload;

   // refactor (remove) type, because we now have only one (filters moved inside columns preset)

   if (type === 'columns') {
    return {
     ...state,
     ...(temp
      ? {
         tempColumnsPreset: {
          ...state.tempColumnsPreset,
          [page]: preset,
         },
        }
      : {
         currentColumnsPreset: {
          ...state.currentColumnsPreset,
          [page]: preset,
         },
        }),
    };
   }

   return state;
  },

  getClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, getClientConfiguration: true },
   };
  },

  getClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    clientConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, getClientConfiguration: true },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  getClientConfigurationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, getClientConfiguration: false },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  getClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfiguration: null },
    loading: { ...state.loading, getClientConfiguration: null },
   };
  },

  getClientConfigurationSelfStart(
   state,
   action: PayloadAction<{ cached?: boolean }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, getClientConfigurationSelf: true },
   };
  },

  getClientConfigurationSelfSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    clientConfigurations: action.payload.data,
    presetsColumns: action.payload.presetsColumns,
    fieldPresets: action.payload.fieldPresets,
    rowCount: action.payload.rowCount,
    success: { ...state.success, getClientConfigurationSelf: true },
    loading: { ...state.loading, getClientConfigurationSelf: null },
   };
  },

  getClientConfigurationSelfFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurationSelf: false },
    loading: { ...state.loading, getClientConfigurationSelf: null },
   };
  },

  getClientConfigurationSelfFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurationSelf: null },
    loading: { ...state.loading, getClientConfigurationSelf: null },
   };
  },

  // multiple

  getClientConfigurationsStart(
   state,
   action: PayloadAction<{ cached?: boolean }>,
  ) {
   return {
    ...state,
    loading: { ...state.loading, getClientConfigurations: true },
   };
  },

  getClientConfigurationsSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    clientConfigurations: action.payload.data,
    presetsColumns: action.payload.presetsColumns,
    fieldPresets: action.payload.fieldPresets,
    rowCount: action.payload.rowCount,
    success: { ...state.success, getClientConfigurations: true },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  getClientConfigurationsFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurations: false },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  getClientConfigurationsFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, getClientConfigurations: null },
    loading: { ...state.loading, getClientConfigurations: null },
   };
  },

  // create

  createClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, createClientConfiguration: true },
   };
  },

  createClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    createdConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    ...(Array.isArray(action.payload.clientConfigurations) && {
     clientConfigurations: action.payload.clientConfigurations,
    }),
    ...(hasValidObjectPayload(action.payload.presetsColumns) && {
     presetsColumns: action.payload.presetsColumns,
    }),
    ...(hasValidObjectPayload(action.payload.fieldPresets) && {
     fieldPresets: action.payload.fieldPresets,
    }),
    success: { ...state.success, createClientConfiguration: true },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  createClientConfigurationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, createClientConfiguration: false },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  createClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, createClientConfiguration: null },
    loading: { ...state.loading, createClientConfiguration: null },
   };
  },

  // update

  updateClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, updateClientConfiguration: true },
   };
  },

  updateClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    updatedConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    ...(Array.isArray(action.payload.clientConfigurations) && {
     clientConfigurations: action.payload.clientConfigurations,
    }),
    ...(hasValidObjectPayload(action.payload.presetsColumns) && {
     presetsColumns: action.payload.presetsColumns,
    }),
    ...(hasValidObjectPayload(action.payload.fieldPresets) && {
     fieldPresets: action.payload.fieldPresets,
    }),
    success: { ...state.success, updateClientConfiguration: true },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  updateClientConfigurationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, updateClientConfiguration: false },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  updateClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, updateClientConfiguration: null },
    loading: { ...state.loading, updateClientConfiguration: null },
   };
  },

  // delete

  deleteClientConfigurationStart(state, action: PayloadAction<any>) {
   return {
    ...state,
    loading: { ...state.loading, deleteClientConfiguration: true },
   };
  },

  deleteClientConfigurationSuccess(state, action: PayloadAction<any>) {
   return {
    ...state,
    deletedConfiguration: action.payload.data,
    rowCount: action.payload.rowCount,
    success: { ...state.success, deleteClientConfiguration: true },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },

  deleteClientConfigurationFailure(state, action: PayloadAction<ErrorType>) {
   return {
    ...state,
    success: { ...state.success, deleteClientConfiguration: false },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },

  deleteClientConfigurationFinish(state, action: PayloadAction<any>) {
   return {
    ...state,
    success: { ...state.success, deleteClientConfiguration: null },
    loading: { ...state.loading, deleteClientConfiguration: null },
   };
  },
 },
});

export const {
 changeCurrentPreset,
 getClientConfigurationStart,
 getClientConfigurationSuccess,
 getClientConfigurationFailure,
 getClientConfigurationFinish,
 getClientConfigurationSelfStart,
 getClientConfigurationSelfSuccess,
 getClientConfigurationSelfFailure,
 getClientConfigurationSelfFinish,
 getClientConfigurationsStart,
 getClientConfigurationsSuccess,
 getClientConfigurationsFailure,
 getClientConfigurationsFinish,
 createClientConfigurationStart,
 createClientConfigurationSuccess,
 createClientConfigurationFailure,
 createClientConfigurationFinish,
 updateClientConfigurationStart,
 updateClientConfigurationSuccess,
 updateClientConfigurationFailure,
 updateClientConfigurationFinish,
 deleteClientConfigurationStart,
 deleteClientConfigurationSuccess,
 deleteClientConfigurationFailure,
 deleteClientConfigurationFinish,
} = clientConfigurationSlice.actions;

export default clientConfigurationSlice.reducer;

